import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck } from "lucide-react";
import CTABand from "@/components/CTABand";
import SpecTable from "@/components/SpecTable";
import { BRANDS, BRAND_ORDER } from "@/data/brands";

export default function BrandDetail() {
  const { slug } = useParams();
  const brand = BRANDS.find((b) => b.id === slug);
  if (!brand) return <Navigate to="/brands" replace />;

  const idx = BRAND_ORDER.indexOf(slug);
  const prevId =
    BRAND_ORDER[(idx - 1 + BRAND_ORDER.length) % BRAND_ORDER.length];
  const nextId = BRAND_ORDER[(idx + 1) % BRAND_ORDER.length];
  const prev = BRANDS.find((b) => b.id === prevId);
  const next = BRANDS.find((b) => b.id === nextId);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 dot-grid opacity-20" />
        <div className="container-nago relative py-16 sm:py-20">
          <Link
            to="/brands"
            className="inline-flex items-center gap-2 text-red-300 hover:text-white text-sm font-semibold"
            data-testid="back-to-brands"
          >
            <ArrowLeft className="w-4 h-4" /> All brands
          </Link>

          <div className="mt-6 grid lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-3">
              <div className="text-xs font-bold uppercase tracking-[0.24em] text-red-300">
                {brand.category}
              </div>
              <h1 className="mt-3 font-display font-extrabold text-4xl sm:text-5xl leading-[1.05] tracking-tight">
                {brand.name}
              </h1>
              {brand.parent && (
                <div className="mt-3 text-sm text-slate-300">
                  <span className="opacity-70">Powered by</span>{" "}
                  <span className="font-semibold">{brand.parent}</span>
                </div>
              )}
              <p className="mt-5 text-slate-200 max-w-2xl leading-relaxed">
                {brand.tagline}
              </p>
              <div className="mt-6 inline-flex items-center gap-2 bg-white/10 border border-white/20 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide">
                <ShieldCheck className="w-3.5 h-3.5 text-red-300" />
                Authorized dealer of {brand.name}
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-white p-6 sm:p-8 rounded-xl border border-white/20 flex items-center justify-center h-40 sm:h-48">
                <img
                  src={brand.logo}
                  alt={`${brand.name} logo`}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section bg-white">
        <div className="container-nago grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="eyebrow">About the brand</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">
              Why customers choose {brand.name}
            </h2>
            <p className="mt-6 text-slate-700 leading-relaxed">
              {brand.description}
            </p>

            {brand.productHighlights?.length ? (
              <ul className="mt-8 grid sm:grid-cols-2 gap-3">
                {brand.productHighlights.map((h) => (
                  <li key={h} className="flex items-start gap-3">
                    <div className="mt-1 w-5 h-5 shrink-0 bg-[#E30613] text-white flex items-center justify-center rounded-xl">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-slate-700 text-sm">{h}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <aside>
            <div className="sticky top-28 bg-slate-50 border border-slate-200 rounded-xl p-6">
              <div className="eyebrow">Enquire now</div>
              <h3 className="mt-2 font-display font-bold text-xl text-slate-900">
                Need a quote for {brand.name} products?
              </h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                Share product, grade, size and quantity — our team will get back
                to you with the right pack and price.
              </p>
              <Link
                to="/contact"
                state={{ brand: brand.name }}
                className="btn-primary w-full mt-5"
                data-testid={`brand-quote-btn-${brand.id}`}
              >
                Request a Quote <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </aside>
        </div>
      </section>

      {/* Products grid */}
      {brand.products?.length ? (
        <section
          className="section bg-slate-50"
          data-testid={`brand-products-${brand.id}`}
        >
          <div className="container-nago">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div>
                <div className="eyebrow">Product range</div>
                <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">
                  {brand.name} products we supply
                </h2>
              </div>
              <div className="text-sm text-slate-600">
                {brand.products.length} product
                {brand.products.length > 1 ? "s" : ""} listed · more sizes
                available on request
              </div>
            </div>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {brand.products.map((p, i) => (
                <div
                  key={`${p.name}-${i}`}
                  className="bg-white border border-slate-200 rounded-xl overflow-hidden flex flex-col hover:shadow-lg hover:border-slate-300 transition-shadow duration-300"
                  data-testid={`product-${brand.id}-${i}`}
                >
                  <div className="relative aspect-square bg-white p-3 flex items-center justify-center border-b border-slate-100">
                    <img
                      src={p.image}
                      alt={p.name}
                      loading="lazy"
                      className="h-48 aspect-3/2 max-w-full object-contain"
                    />
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-display font-bold text-sm sm:text-base text-slate-900 leading-tight">
                      {p.name}
                    </h3>
                    <div className="mt-1.5 text-xs text-slate-600">
                      {p.spec}
                    </div>
                    <div className="mt-3 inline-flex items-center self-start bg-[#1E3A8A]/10 text-[#1E3A8A] px-2 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-md">
                      {p.size}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : (
        <section className="section bg-slate-50">
          <div className="container-nago">
            <div className="bg-white border border-slate-200 rounded-xl p-8 sm:p-12 text-center">
              <div className="eyebrow">Product listing coming soon</div>
              <h3 className="mt-3 font-display font-bold text-2xl text-slate-900">
                Detailed {brand.name} product images are being added.
              </h3>
              <p className="mt-3 text-sm text-slate-600 max-w-lg mx-auto">
                In the meantime, share your requirement and our team will get
                back with the complete range and pricing.
              </p>
              <Link
                to="/contact"
                className="btn-primary mt-6"
                data-testid="cta-contact-empty"
              >
                Send Enquiry <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Grades / Sizes table */}
      {brand.grades ? (
        <section className="section bg-white">
          <div className="container-nago max-w-4xl">
            <div className="eyebrow">Grades & sizes</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-slate-900">
              What we stock &amp; source
            </h2>
            <SpecTable {...brand.grades} />
            <p className="mt-6 text-xs text-slate-500">
              Sizes and grades listed are representative. Additional sizes and
              specialty variants available on request.
            </p>
          </div>
        </section>
      ) : null}

      {/* Prev / Next */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="container-nago py-8 grid sm:grid-cols-2 gap-4">
          <Link
            to={`/brands/${prev.id}`}
            className="group flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-xl hover:border-[#1E3A8A] transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-slate-400 group-hover:text-[#E30613]" />
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">
                Previous brand
              </div>
              <div className="mt-1 font-display font-semibold text-slate-900">
                {prev.name}
              </div>
            </div>
          </Link>
          <Link
            to={`/brands/${next.id}`}
            className="group flex items-center gap-4 p-5 bg-white border border-slate-200 rounded-xl hover:border-[#1E3A8A] transition-colors sm:justify-end sm:text-right"
          >
            <div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500">
                Next brand
              </div>
              <div className="mt-1 font-display font-semibold text-slate-900">
                {next.name}
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-[#E30613]" />
          </Link>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
